/**
 * Sticky panels for Product specification text/image.
 *
 * Uses native CSS position:sticky when ancestors allow it.
 * Falls back to translateY when an ancestor uses overflow clip/hidden
 * (e.g. closed accordion animation), and always tracks .page-wrapper scroll.
 *
 * Targets: [data-sticky-panel]
 */
const SELECTOR = '[data-sticky-panel]';
const MOBILE_MQ = window.matchMedia('(max-width: 749px)');
const DEFAULT_GAP = 20;

/**
 * @param {HTMLElement} el
 * @returns {boolean}
 */
function hasClippingAncestor(el) {
  let parent = el.parentElement;
  while (parent && parent !== document.body) {
    const style = getComputedStyle(parent);
    const overflowY = style.overflowY;
    if (/(hidden|clip|scroll|auto)/.test(overflowY) && parent.scrollHeight > parent.clientHeight + 1) {
      // Scrollable ancestors are OK for sticky; only clip/hidden without being the page scroll root break it
      if (overflowY === 'hidden' || overflowY === 'clip') return true;
    }
    if (overflowY === 'clip' || (overflowY === 'hidden' && style.overflowX === 'hidden')) {
      return true;
    }
    parent = parent.parentElement;
  }
  return false;
}

/**
 * @param {HTMLElement} el
 * @returns {HTMLElement | Window}
 */
function getScrollRoot(el) {
  const pageWrapper = document.querySelector('.page-wrapper');
  if (pageWrapper instanceof HTMLElement) {
    const style = getComputedStyle(pageWrapper);
    if (/(auto|scroll|overlay)/.test(style.overflowY)) return pageWrapper;
  }

  let parent = el.parentElement;
  while (parent && parent !== document.body) {
    const style = getComputedStyle(parent);
    if (/(auto|scroll|overlay)/.test(style.overflowY) && parent.scrollHeight > parent.clientHeight + 1) {
      return parent;
    }
    parent = parent.parentElement;
  }
  return window;
}

/**
 * @param {HTMLElement} el
 * @returns {HTMLElement}
 */
function getBoundary(el) {
  return (
    el.closest('.group-block-content') ||
    el.closest('.details-content') ||
    el.parentElement ||
    el
  );
}

/**
 * @param {HTMLElement} el
 * @returns {number}
 */
function getGap(el) {
  const styles = getComputedStyle(el);
  const raw =
    styles.getPropertyValue('--text-sticky-offset').trim() ||
    styles.getPropertyValue('--product-spec-image-sticky-offset').trim() ||
    `${DEFAULT_GAP}px`;
  const value = Number.parseFloat(raw);
  return Number.isFinite(value) ? value : DEFAULT_GAP;
}

/**
 * @returns {number}
 */
function getHeaderHeight() {
  const fromBody = getComputedStyle(document.body).getPropertyValue('--header-height').trim();
  const fromRoot = getComputedStyle(document.documentElement)
    .getPropertyValue('--header-height')
    .trim();
  const value = Number.parseFloat(fromBody || fromRoot || '60');
  return Number.isFinite(value) ? value : 60;
}

/**
 * @param {HTMLElement} el
 * @returns {number}
 */
function getOffset(el) {
  return getHeaderHeight() + getGap(el);
}

/**
 * @param {HTMLElement} el
 * @returns {number}
 */
function getNaturalTop(el) {
  const rect = el.getBoundingClientRect();
  const transform = getComputedStyle(el).transform;
  if (!transform || transform === 'none') return rect.top;
  try {
    return rect.top - new DOMMatrixReadOnly(transform).m42;
  } catch {
    return rect.top;
  }
}

/**
 * @param {HTMLElement} el
 */
function clearJsSticky(el) {
  el.style.transform = '';
  el.style.willChange = '';
}

/**
 * @param {HTMLElement} el
 */
function updatePanel(el) {
  // Mobile: no sticky — stacked layout, image is full width
  if (MOBILE_MQ.matches) {
    clearJsSticky(el);
    return;
  }

  // Prefer native CSS sticky unless an ancestor clips overflow
  if (!hasClippingAncestor(el)) {
    clearJsSticky(el);
    return;
  }

  const boundary = getBoundary(el);
  const offset = getOffset(el);
  const elHeight = el.offsetHeight;
  const maxTravel = Math.max(0, boundary.offsetHeight - elHeight);

  if (maxTravel <= 0) {
    clearJsSticky(el);
    return;
  }

  const naturalTop = getNaturalTop(el);
  const boundaryBottom = boundary.getBoundingClientRect().bottom;
  const maxTop = boundaryBottom - elHeight;

  let travel = 0;
  if (naturalTop < offset) {
    const pinnedTop = Math.min(offset, maxTop);
    travel = Math.min(maxTravel, Math.max(0, pinnedTop - naturalTop));
  }

  if (travel > 0) {
    el.style.willChange = 'transform';
    el.style.transform = `translate3d(0, ${travel}px, 0)`;
  } else {
    clearJsSticky(el);
  }
}

function updateAll() {
  document.querySelectorAll(SELECTOR).forEach((node) => {
    if (node instanceof HTMLElement) updatePanel(node);
  });
}

/** @type {WeakMap<HTMLElement | Window, true>} */
const boundRoots = new WeakMap();

/**
 * @param {HTMLElement | Window} root
 */
function bindScrollRoot(root) {
  if (boundRoots.has(root)) return;
  boundRoots.set(root, true);
  const target = root === window ? window : root;
  target.addEventListener('scroll', updateAll, { passive: true });
}

/**
 * @param {HTMLElement} el
 */
function observePanel(el) {
  bindScrollRoot(getScrollRoot(el));
  bindScrollRoot(window);
  const pageWrapper = document.querySelector('.page-wrapper');
  if (pageWrapper instanceof HTMLElement) bindScrollRoot(pageWrapper);
  updatePanel(el);
}

function init() {
  document.querySelectorAll(SELECTOR).forEach((node) => {
    if (node instanceof HTMLElement) observePanel(node);
  });
  updateAll();
}

window.addEventListener('resize', updateAll, { passive: true });
MOBILE_MQ.addEventListener('change', updateAll);
document.addEventListener('toggle', updateAll, true);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

document.addEventListener('shopify:section:load', init);
document.addEventListener('shopify:block:select', updateAll);
